import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2, ExternalLink, Inbox, Loader2,
  RefreshCw, Trash2, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getJobDrafts, bulkPublishJobs, bulkDeleteJobs } from '@/lib/api';

const JOBS_FALLBACK_IMG = 'https://paisley.org.uk/jobs-placeholder.jpg';
const JOBS_FALLBACK_ALT = 'Jobs in Paisley, Renfrewshire & Glasgow';

export const JobsReviewQueue = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [working, setWorking] = useState(false);
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getJobDrafts();
      setDrafts(res.data || []);
      setSelected(new Set());
    } catch {
      toast.error('Failed to load draft jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sources = useMemo(() => {
    const list = [];
    for (const d of drafts) {
      const src = d.imported_from || d.source || 'manual';
      if (!list.includes(src)) list.push(src);
    }
    return ['all', ...list];
  }, [drafts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return drafts.filter(d => {
      if (sourceFilter !== 'all') {
        const src = d.imported_from || d.source || 'manual';
        if (src !== sourceFilter) return false;
      }
      if (!q) return true;
      return [d.title, d.employer, d.location].some(
        v => (v || '').toLowerCase().includes(q),
      );
    });
  }, [drafts, query, sourceFilter]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every(d => selected.has(d.id));

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach(d => next.delete(d.id));
      } else {
        filtered.forEach(d => next.add(d.id));
      }
      return next;
    });
  };

  const handlePublish = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one job');
      return;
    }
    setWorking(true);
    try {
      const res = await bulkPublishJobs(Array.from(selected));
      toast.success(res.data.message || `Published ${res.data.published}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk publish failed');
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one job');
      return;
    }
    setWorking(true);
    try {
      const res = await bulkDeleteJobs(Array.from(selected));
      toast.success(res.data.message || `Deleted ${res.data.deleted}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div data-testid="jobs-review-queue">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="h-7 w-7 text-sky-500" /> Jobs Review Queue
          </h1>
          <p className="text-slate-600">
            Drafts pending review — bulk publish or reject ClydeFlow imports.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-drafts">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filter / Bulk toolbar */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex items-center gap-2 lg:mr-auto">
            <Checkbox
              checked={allFilteredSelected}
              onCheckedChange={toggleAll}
              disabled={filtered.length === 0}
              data-testid="select-all-drafts"
            />
            <span className="text-sm text-slate-600">
              {selected.size > 0
                ? <span className="font-medium text-slate-900">{selected.size} selected</span>
                : `Select all (${filtered.length})`}
            </span>
          </div>
          <Input
            placeholder="Search title, employer, location…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="lg:w-72"
            data-testid="search-drafts-input"
          />
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="lg:w-48" data-testid="source-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {sources.map(s => (
                <SelectItem key={s} value={s}>
                  {s === 'all' ? 'All sources' : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                  disabled={selected.size === 0 || working}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Reject
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" /> Delete drafts?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove <strong>{selected.size}</strong> draft job{selected.size === 1 ? '' : 's'}. They will not be published.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-500 hover:bg-red-600"
                    data-testid="confirm-bulk-delete"
                  >
                    Delete {selected.size}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button
              onClick={handlePublish}
              disabled={selected.size === 0 || working}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="bulk-publish-btn"
            >
              {working
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Working…</>
                : <><CheckCircle2 className="h-4 w-4 mr-2" /> Publish {selected.size > 0 ? `(${selected.size})` : ''}</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Drafts grid */}
      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />
          Loading drafts…
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Inbox className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <h3 className="font-medium text-slate-700 mb-1">No drafts to review</h3>
            <p className="text-sm text-slate-500">
              {drafts.length === 0
                ? 'When ClydeFlow imports jobs as drafts they will appear here for bulk approval.'
                : 'Try removing the search/source filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(job => {
            const src = job.imported_from || job.source || 'manual';
            const isSelected = selected.has(job.id);
            return (
              <Card
                key={job.id}
                className={`relative transition-all cursor-pointer overflow-hidden ${
                  isSelected ? 'ring-2 ring-sky-400 shadow-md' : 'hover:shadow'
                }`}
                onClick={() => toggleOne(job.id)}
                data-testid={`draft-card-${job.id}`}
              >
                <div className="absolute top-3 left-3 z-10">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleOne(job.id)}
                    className="bg-white shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {job.cover_image ? (
                  <div className="h-32 bg-slate-100 overflow-hidden">
                    <img
                      src={job.cover_image}
                      alt={`${job.title} — ${job.employer}`}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        if (e.target.src !== JOBS_FALLBACK_IMG) e.target.src = JOBS_FALLBACK_IMG;
                      }}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="h-32 bg-slate-100 overflow-hidden">
                    <img
                      src={JOBS_FALLBACK_IMG}
                      alt={JOBS_FALLBACK_ALT}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <CardTitle className="text-base line-clamp-2">{job.title}</CardTitle>
                  <CardDescription className="text-xs">
                    <span className="font-medium text-slate-700">{job.employer || 'Various employers'}</span>
                    {job.location && <span className="text-slate-500"> · {job.location}</span>}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-4 space-y-2">
                  {job.salary && (
                    <p className="text-xs text-slate-600">{job.salary}</p>
                  )}
                  {(job.summary || job.description) && (
                    <p className="text-xs text-slate-500 line-clamp-2">
                      {job.summary || job.description}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Badge variant="outline" className="text-[10px]">
                      {src}
                    </Badge>
                    {job.date_posted && (
                      <span className="text-[10px] text-slate-400">
                        {job.date_posted}
                      </span>
                    )}
                    {job.link && (
                      <a
                        href={job.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-[11px] text-sky-600 hover:underline inline-flex items-center gap-0.5 ml-auto"
                      >
                        Source <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JobsReviewQueue;
